/**
 * Demo-specific configuration for StablePay presentation demo
 * This configuration enables mock API mode and demo-specific features
 */

export interface DemoConfig {
  // API Configuration
  apiMode: 'mock' | 'production';
  mockApiDelay: number;
  enableRealTimeUpdates: boolean;
  
  // Feature Flags
  features: {
    providerFailover: boolean;
    settlementRailDemo: boolean;
    costCalculator: boolean;
    professionalBranding: boolean;
    demoControlPanel: boolean;
    offlineMode: boolean;
  };
  
  // Demo Assets
  assets: {
    logoVariant: 'full-color' | 'white' | 'monochrome';
    theme: 'professional' | 'demo';
    showDemoWatermark: boolean;
  };
  
  // Mock Data Configuration
  mockData: {
    businessProfile: {
      name: string;
      type: 'SME' | 'Enterprise' | 'Startup';
      industry: string;
      monthlyVolume: number;
    };
    transactionDefaults: {
      amount: number;
      currency: string;
      stablecoin: string;
    };
    providerHealth: {
      simulateFailures: boolean;
      failureRate: number;
      recoveryTime: number;
    };
  };
  
  // Performance Settings
  performance: {
    enableCodeSplitting: boolean;
    enableCaching: boolean;
    optimizeAssets: boolean;
  };
  
  // Analytics and Tracking
  analytics: {
    enabled: boolean;
    trackDemoInteractions: boolean;
    debugMode: boolean;
  };
}

export const demoConfig: DemoConfig = {
  apiMode: 'mock',
  mockApiDelay: 300, // 300ms realistic delay
  enableRealTimeUpdates: true,
  
  features: {
    providerFailover: true,
    settlementRailDemo: true,
    costCalculator: true,
    professionalBranding: true,
    demoControlPanel: true,
    offlineMode: true,
  },
  
  assets: {
    logoVariant: 'full-color',
    theme: 'professional',
    showDemoWatermark: false, // Set to true for internal demos
  },
  
  mockData: {
    businessProfile: {
      name: 'Hong Kong Trading Co.',
      type: 'SME',
      industry: 'Import/Export',
      monthlyVolume: 500000, // HKD
    },
    transactionDefaults: {
      amount: 10000,
      currency: 'HKD',
      stablecoin: 'USDT',
    },
    providerHealth: {
      simulateFailures: true,
      failureRate: 0.05, // 5% failure rate for demo
      recoveryTime: 30000, // 30 seconds recovery
    },
  },
  
  performance: {
    enableCodeSplitting: true,
    enableCaching: true,
    optimizeAssets: true,
  },
  
  analytics: {
    enabled: false, // Disabled for demo
    trackDemoInteractions: true,
    debugMode: process.env.NODE_ENV === 'development',
  },
};

// Environment-specific overrides
export const getEnvironmentConfig = (): Partial<DemoConfig> => {
  const env = process.env.NODE_ENV || 'development';
  
  switch (env) {
    case 'development':
      return {
        analytics: {
          enabled: false,
          trackDemoInteractions: true,
          debugMode: true,
        },
        assets: {
          logoVariant: 'full-color',
          theme: 'professional',
          showDemoWatermark: true,
        },
      };
      
    case 'production':
      return {
        analytics: {
          enabled: false,
          trackDemoInteractions: false,
          debugMode: false,
        },
        assets: {
          logoVariant: 'full-color',
          theme: 'professional',
          showDemoWatermark: false,
        },
        performance: {
          enableCodeSplitting: true,
          enableCaching: true,
          optimizeAssets: true,
        },
      };
      
    case 'demo':
      return {
        features: {
          providerFailover: true,
          settlementRailDemo: true,
          costCalculator: true,
          professionalBranding: true,
          demoControlPanel: true,
          offlineMode: true,
        },
        assets: {
          logoVariant: 'full-color',
          theme: 'professional',
          showDemoWatermark: false,
        },
      };
      
    default:
      return {};
  }
};

// Merge base config with environment-specific overrides
export const getFinalDemoConfig = (): DemoConfig => {
  const envConfig = getEnvironmentConfig();
  return {
    ...demoConfig,
    ...envConfig,
    features: {
      ...demoConfig.features,
      ...envConfig.features,
    },
    assets: {
      ...demoConfig.assets,
      ...envConfig.assets,
    },
    analytics: {
      ...demoConfig.analytics,
      ...envConfig.analytics,
    },
    performance: {
      ...demoConfig.performance,
      ...envConfig.performance,
    },
  };
};